'use client';

import * as React from 'react';
import { usePathname } from 'next/navigation';

import { AnalyticsProvider as AnalyticsProviderImpl } from '../provider';
import type { AnalyticsProvider as AnalyticsProviderInterface } from '../provider/types';

function normalizePath(pathname: string): string {
  const path = pathname.split('?')[0] ?? pathname;
  return path
    .replace(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      '[id]'
    )
    .replace(/\/organizations\/[^/]+/, '/organizations/[slug]');
}

function PageViewTracker(): null {
  const pathname = usePathname();

  React.useEffect(() => {
    AnalyticsProviderImpl.trackPageView(normalizePath(pathname));
  }, [pathname]);

  return null;
}

type UserIdentificationData = {
  id: string;
  email?: string;
  name?: string;
  organizationId?: string;
  organizationName?: string;
  role?: string;
  tier?: string;
};

export type AnalyticsProviderProps = React.PropsWithChildren<{
  user?: UserIdentificationData;
}>;

export const AnalyticsContext = React.createContext<AnalyticsProviderInterface>(
  AnalyticsProviderImpl
);

export function AnalyticsProvider({
  user,
  children
}: AnalyticsProviderProps): React.JSX.Element {
  // Identify user when user data is provided
  React.useEffect(() => {
    if (user?.id) {
      const traits: Record<string, string | number | boolean> = {};

      if (user.email) traits.$email = user.email;
      if (user.name) traits.$name = user.name;
      if (user.organizationId) traits.organizationId = user.organizationId;
      if (user.organizationName)
        traits.organizationName = user.organizationName;
      if (user.role) traits.role = user.role;
      if (user.tier) traits.tier = user.tier;

      AnalyticsProviderImpl.identify(user.id, traits);
    }
  }, [user]);

  return (
    <AnalyticsContext.Provider value={AnalyticsProviderImpl}>
      <React.Suspense fallback={null}>
        <PageViewTracker />
      </React.Suspense>
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalytics(): AnalyticsProviderInterface {
  return React.useContext(AnalyticsContext);
}
